/**
 * The search results panel is a PROJECTION of the search agent's own tool
 * calls, never a second query the client issued
 * (app-shell ADR-021). Two sources for one list is two answers, and the list beside the
 * conversation would be free to disagree with it.
 *
 * The chat surface PUBLISHES its event log (`chatSurfaceEventsAtom`); this
 * reads the newest `search` tool result from it and hydrates the full payload,
 * which a large event's skeleton withholds. Reading the surface's own published
 * log rather than the SDK's singleton session atom keeps this independent of
 * which component owns the session and when it resolves.
 */

import { useEffect, useState } from 'react';
import { useAtomValue } from 'jotai';
import type { SearchRequest, SearchResult } from '@localagent/sdk';
import { getSDK } from '../../services/sdk-instance';
import { chatSurfaceEventsAtom } from '../../stores/chat/session-events';

const SEARCH_TOOL_NAME = 'search';

export interface AgentSearchState {
  results: SearchResult[];
  /** A search is in flight — the agent called the tool and no result has landed. */
  isSearching: boolean;
  /** The agent's last search failed; the tool reported it rather than returning nothing. */
  error: string | null;
  /** True until the agent has run its first search. */
  awaitingFirstSearch: boolean;
}

interface SearchToolEvent {
  sequence_num: number;
  event_type: string;
  data?: Record<string, unknown>;
}

function isSearchResultEvent(e: SearchToolEvent): boolean {
  return e.event_type === 'tool_call_result' && e.data?.tool_name === SEARCH_TOOL_NAME;
}

function isSearchStartEvent(e: SearchToolEvent): boolean {
  return e.event_type === 'tool_call_start' && e.data?.tool_name === SEARCH_TOOL_NAME;
}

/**
 * Every `search` result since the user's most recent message — the agent's
 * answer to THIS question, which may have taken several searches. The agent is
 * told to refine a thin first search, so showing only the last call would let a
 * narrowing refinement drop results it had already found and shown.
 *
 * Scoped to the current turn so a new question starts a new list rather than
 * accumulating across the conversation.
 */
/**
 * The agent's own recorded arguments for a `search` call. A tool result's
 * `result_preview` is a BOUNDED preview — hard-capped around 2KB, with the full
 * payload claim-checked behind a `result://` handle the client cannot read — so
 * anything but a small result arrives truncated and unparseable. The arguments
 * do NOT truncate (measured at 97-132 bytes), which is what makes re-running
 * the agent's exact query the way to recover full fidelity.
 */
export function parseSearchArgs(argsPreview: unknown): SearchRequest | null {
  if (typeof argsPreview !== 'string') return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(argsPreview);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const a = parsed as Record<string, unknown>;
  if (typeof a.query !== 'string' || a.query.length === 0) return null;
  const req: SearchRequest = { query: a.query };
  if (typeof a.mode === 'string') req.mode = a.mode as SearchRequest['mode'];
  if (typeof a.sort === 'string') req.sort = a.sort as SearchRequest['sort'];
  if (typeof a.scope === 'string') req.scope = a.scope as SearchRequest['scope'];
  if (Array.isArray(a.entity_types)) req.entity_types = a.entity_types as SearchRequest['entity_types'];
  if (Array.isArray(a.workspace_id)) req.workspace_id = a.workspace_id as string[];
  if (typeof a.limit === 'number') req.limit = a.limit;
  return req;
}

/** Pair each `search` result with the call that produced it, in order. */
export function pairSearchCallsForCurrentTurn(
  events: readonly SearchToolEvent[],
): { startSeq: number; resultSeq: number }[] {
  let turnStart = -1;
  for (const e of events) {
    if (e.event_type === 'user_message') turnStart = e.sequence_num;
  }
  const pairs: { startSeq: number; resultSeq: number }[] = [];
  let pendingStart: number | null = null;
  for (const e of events) {
    if (e.sequence_num <= turnStart) continue;
    if (isSearchStartEvent(e)) pendingStart = e.sequence_num;
    else if (isSearchResultEvent(e)) {
      pairs.push({ startSeq: pendingStart ?? -1, resultSeq: e.sequence_num });
      pendingStart = null;
    }
  }
  return pairs;
}

export function searchResultSeqsForCurrentTurn(events: readonly SearchToolEvent[]): number[] {
  let turnStart = -1;
  for (const e of events) {
    if (e.event_type === 'user_message') turnStart = e.sequence_num;
  }
  return events
    .filter((e) => e.sequence_num > turnStart && isSearchResultEvent(e))
    .map((e) => e.sequence_num);
}

/** Union preserving first-seen order; a result is identified by type + id. */
export function mergeSearchResults(batches: readonly SearchResult[][]): SearchResult[] {
  const seen = new Set<string>();
  const merged: SearchResult[] = [];
  for (const batch of batches) {
    for (const r of batch) {
      const key = `${r.entity_type}:${r.entity_id}`;
      if (seen.has(key)) continue;
      seen.add(key);
      merged.push(r);
    }
  }
  return merged;
}

/** Pull the result array out of the tool's JSON `SearchResponse` payload. */
export function parseSearchToolPayload(content: unknown): SearchResult[] | null {
  const raw =
    typeof content === 'string'
      ? content
      : content && typeof content === 'object'
        ? JSON.stringify(content)
        : null;
  if (raw === null) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (!parsed || typeof parsed !== 'object') return null;
  const results = (parsed as { results?: unknown }).results;
  return Array.isArray(results) ? (results as SearchResult[]) : null;
}

export function useAgentSearchResults(): AgentSearchState {
  const surface = useAtomValue(chatSurfaceEventsAtom);
  const [results, setResults] = useState<SearchResult[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [isSearching, setIsSearching] = useState(false);
  const [awaitingFirstSearch, setAwaitingFirstSearch] = useState(true);

  useEffect(() => {
    if (!surface) return;
    const events = surface.events as unknown as SearchToolEvent[];

    let latestStart: SearchToolEvent | null = null;
    for (const e of events) {
      if (isSearchStartEvent(e)) latestStart = e;
    }
    const pairs = pairSearchCallsForCurrentTurn(events);

    if (pairs.length === 0) {
      // A call is out and nothing has come back yet.
      setIsSearching(latestStart !== null);
      return;
    }
    setAwaitingFirstSearch(false);
    const newestResult = pairs[pairs.length - 1].resultSeq;
    setIsSearching(latestStart !== null && latestStart.sequence_num > newestResult);

    let cancelled = false;

    /**
     * One call's results. The preview is authoritative when it parses; when it
     * does not it was TRUNCATED (a bounded preview, ~2KB), and the only way to
     * the full set is to re-run the agent's own recorded arguments. That is
     * still the agent's query, not one this view invented.
     */
    const resultsForCall = async (
      startSeq: number,
      resultSeq: number,
    ): Promise<SearchResult[]> => {
      const full = await surface.getEventData(resultSeq);
      if (full.status === 'error') {
        throw new Error(typeof full.error === 'string' ? full.error : 'The search failed.');
      }
      const parsed = parseSearchToolPayload(full.result_preview);
      if (parsed !== null) return parsed;

      if (startSeq < 0) {
        throw new Error('A search result was truncated and its query was not recorded.');
      }
      const startData = await surface.getEventData(startSeq);
      const args = parseSearchArgs(startData.args_preview);
      if (args === null) {
        throw new Error('A search result was truncated and its query could not be read.');
      }
      const response = await getSDK().search.search(args);
      return response.results;
    };

    void Promise.all(pairs.map((p) => resultsForCall(p.startSeq, p.resultSeq)))
      .then((batches) => {
        if (cancelled) return;
        setError(null);
        setResults(mergeSearchResults(batches));
      })
      .catch((err: unknown) => {
        if (cancelled) return;
        // Loud: a result the panel cannot render is a broken panel, not an
        // empty one (.claude/rules/no-fallbacks.md).
        setError(err instanceof Error ? err.message : String(err));
        setResults([]);
      });
    return () => {
      cancelled = true;
    };
  }, [surface]);

  return { results, isSearching, error, awaitingFirstSearch };
}
